"use client";

import React, { useEffect, useState, useRef, useCallback, useMemo, CSSProperties, ReactNode, RefObject } from "react";
import { createPortal } from 'react-dom';

import Button, { IconButton, Pressable } from './Button';

// i18n 默认文本（调用者应该传入翻译后的 okText/cancelText）
const DEFAULT_OK_TEXT = "确定";
const DEFAULT_CANCEL_TEXT = "取消";

import { Cross2Icon } from '@radix-ui/react-icons';

import clsx from "clsx";

import styleClasses from './Modal.module.css';

import * as ReactDOM from 'react-dom/client';

import TextInput from 'bwax-ui/components/inputs/TextInput';
import Checkbox from "bwax-ui/components/inputs/Checkbox";
import TextArea from "bwax-ui/components/inputs/TextArea";
import PasswordInput from "bwax-ui/components/inputs/PasswordInput.js";
import SelectInput from "bwax-ui/components/inputs/SelectInput";


// ==================== 类型定义 ====================

interface OverlayTriggerStateProps {
    isOpen?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
}

interface OverlayTriggerState {
    isOpen: boolean;
    setOpen: (value: boolean) => void;
    open: () => void;
    close: () => void;
    toggle: () => void;
}

interface ModalBehaviorOptions {
    isOpen: boolean;
    onClose?: () => void;
    isDismissable?: boolean;
}

interface ModalStatelessProps {
    modalRef?: RefObject<HTMLDivElement>;
    state: OverlayTriggerState;
    zIndex?: number;
    children: ReactNode;
    className?: string;
    contentClassName?: string;
    contentRef?: RefObject<HTMLDivElement> | ((node: HTMLDivElement | null) => void);
    isMain?: boolean;
    closeButton?: boolean;
    isLarge?: boolean;
    isTransparent?: boolean;
    style?: CSSProperties;
    isDismissable?: boolean;
}

interface ModalProps extends Omit<OverlayTriggerStateProps, 'isOpen'> {
    isOpen?: boolean;
    children: ReactNode | ((close: () => void) => ReactNode);
    modalRef?: RefObject<HTMLDivElement>;
    zIndex?: number;
    className?: string;
    contentClassName?: string;
    contentRef?: RefObject<HTMLDivElement> | ((node: HTMLDivElement | null) => void);
    isMain?: boolean;
    closeButton?: boolean;
    isLarge?: boolean;
    isTransparent?: boolean;
    style?: CSSProperties;
    isDismissable?: boolean;
}

interface ModalTriggerProps extends OverlayTriggerStateProps {
    modalClassName?: string;
    className?: string;
    content: ReactNode | ((close: () => void) => ReactNode);
    children: React.ReactElement;
    onBeforePress?: () => void;
    modalRef?: RefObject<HTMLDivElement>;
    zIndex?: number;
    contentClassName?: string;
    contentRef?: RefObject<HTMLDivElement> | ((node: HTMLDivElement | null) => void);
    isMain?: boolean;
    closeButton?: boolean;
    isLarge?: boolean;
    isTransparent?: boolean;
    style?: CSSProperties;
    isDismissable?: boolean;
}

interface ModalConfirmProps {
    content: ReactNode;
    error?: string;
    isProgressing?: boolean;
    isReady?: boolean;
    cancelText?: string;
    okText?: string;
    okColor?: string;
    onCancel?: () => void;
    onOk?: () => void;
    okAppearance?: 'primary' | 'default' | 'outline' | 'borderless';
    noCancel?: boolean;
    style?: CSSProperties;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

interface CreateModalConfirmOptions {
    cancelText?: string;
    noCancel?: boolean;
    okText?: string;
    okColor?: string;
    content: ReactNode;
    onConfirm?: (yes: boolean, dismissed?: boolean) => void;
    okAppearance?: 'primary' | 'default' | 'outline' | 'borderless';
    style?: CSSProperties;
}

interface ModalFormField {
    name: string;
    label?: string;
    placeholder?: string;
    type?: 'Boolean' | 'Password' | 'Select' | 'Text';
    initialValue?: any;
    minRows?: number;
    options?: any[];
    isMulti?: boolean;
    [key: string]: any;
}

interface ModalFormProps {
    onOpenChange: (open: boolean) => void;
    onDataCollected: (data: Record<string, any>) => Promise<void> | void;
    onCancel?: () => void;
    color?: string;
    isSubmittable?: (data: Record<string, any>) => boolean;
    additionalContent?: ReactNode;
    okText?: string;
    fields?: ModalFormField[];
    title?: string;
    description?: string;
    showLabel?: boolean;
}

interface CreateModalFormOptions {
    okText?: string;
    fields: ModalFormField[];
    title?: string;
    description?: string;
    showLabel?: boolean;
    color?: string;
    isSubmittable?: ((data: Record<string, any>) => boolean) | null;
    onDataCollected: (data: Record<string, any>) => Promise<void> | void;
    onCancel?: () => void;
    additionalContent?: ReactNode;
}


// ==================== Hooks ====================

// 自定义 Hook：管理 overlay trigger 状态
function useOverlayTriggerState({ 
    isOpen: controlledIsOpen, 
    defaultOpen = false, 
    onOpenChange 
}: OverlayTriggerStateProps): OverlayTriggerState {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
    
    const isControlled = controlledIsOpen !== undefined;
    const isOpen = isControlled ? controlledIsOpen : uncontrolledOpen;
    
    const setOpen = useCallback((value: boolean) => {
        if (!isControlled) {
            setUncontrolledOpen(value);
        }
        onOpenChange?.(value);
    }, [isControlled, onOpenChange]);
    
    return {
        isOpen,
        setOpen,
        open: () => setOpen(true),
        close: () => setOpen(false),
        toggle: () => setOpen(!isOpen),
    };
}

// 自定义 Hook：管理焦点陷阱和键盘事件
function useModalBehavior(
    { isOpen, onClose, isDismissable = true }: ModalBehaviorOptions, 
    ref: RefObject<HTMLDivElement>
) {
    const previousActiveElementRef = useRef<HTMLElement | null>(null);
    
    useEffect(() => {
        if (!isOpen) return;
        
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isDismissable) {
                e.preventDefault();
                onClose?.();
            }
        };
        
        // 保存之前的焦点（只在首次打开时保存）
        if (!previousActiveElementRef.current) {
            previousActiveElementRef.current = document.activeElement as HTMLElement;
        }
        
        document.addEventListener('keydown', handleKeyDown);
        
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose, isDismissable]);
    
    // 仅在模态框关闭时恢复焦点
    useEffect(() => {
        return () => {
            // 恢复焦点
            if (previousActiveElementRef.current instanceof HTMLElement) {
                previousActiveElementRef.current.focus();
                previousActiveElementRef.current = null;
            }
        };
    }, []);
}


// ==================== 组件 ====================

// Overlay Portal 组件
function Overlay({ children }: { children: ReactNode }) {
    const [mounted, setMounted] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    
    useEffect(() => {
        // 创建独立的容器
        if (!containerRef.current) {
            containerRef.current = document.createElement('div');
            containerRef.current.setAttribute('data-modal-overlay', 'true');
            document.body.appendChild(containerRef.current);
        }
        
        setMounted(true);
        
        return () => {
            setMounted(false);
            
            // 延迟清理容器，确保 React 先完成内部清理
            const container = containerRef.current;
            if (container) {
                // 使用微任务延迟，让 React 的 unmount 先完成
                Promise.resolve().then(() => {
                    try {
                        if (container.parentNode === document.body) {
                            document.body.removeChild(container);
                        }
                    } catch (e) {
                        console.warn('Overlay cleanup error:', e);
                    }
                });
                containerRef.current = null;
            }
        };
    }, []);
    
    if (!mounted || !containerRef.current) return null;
    
    return createPortal(children, containerRef.current);
}


export function ModalStateless({
    modalRef, 
    state, 
    zIndex, 
    children, 
    className, 
    contentClassName, 
    contentRef, 
    isMain = true, 
    closeButton = true, 
    isLarge = false, 
    isTransparent = false,
    style,
    isDismissable = false,
}: ModalStatelessProps) {
    const ref = useRef<HTMLDivElement>(null!);
    const isMounted = useRef<boolean>(true);

    useModalBehavior({ 
        isOpen: state.isOpen, 
        onClose: state.close, 
        isDismissable 
    }, ref);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const handleUnderlayClick = (e: React.MouseEvent) => {
        // 只有点击 underlay 自身时才关闭，避免事件冒泡导致子元素失去焦点
        if (e.target === e.currentTarget && isDismissable) {
            e.stopPropagation();
            state.close();
        }
    };

    const handleModalClick = (e: React.MouseEvent) => {
        // 阻止模态框内部点击事件冒泡到 underlay
        e.stopPropagation();
    };

    return (
        <Overlay>
            <div 
                className={styleClasses["lc-modal-underlay"]} 
                style={{ zIndex }}
                onClick={handleUnderlayClick}
                onMouseDown={(e) => {
                    // 防止在输入框输入时，underlay 的 mousedown 导致焦点丢失
                    if (e.target === e.currentTarget && isDismissable) {
                        e.preventDefault();
                    }
                }}
            >
                <div 
                    tabIndex={-1}
                    onClick={handleModalClick}
                    style={isTransparent ? {
                        height: "unset",
                        width: "unset",
                        background: "transparent",
                        ...(style || {})
                    } : style}
                    className={
                        clsx(
                            styleClasses["lc-modal"],
                            "lc-base font-size-14 lc-with-bg-2", 
                            className,
                            {
                                "is-large": isLarge,
                                "is-transparent": isTransparent,
                            }
                        )
                    }
                    ref={(r) => {
                        if (ref) {
                            (ref as React.MutableRefObject<HTMLDivElement | null>).current = r;
                        }
                        if (modalRef) { 
                            (modalRef as React.MutableRefObject<HTMLDivElement | null>).current = r;
                        }
                    }} 
                    data-main={isMain}
                >
                    <div 
                        className={clsx(styleClasses["lc-modal-content"], contentClassName)} 
                        ref={(r) => {
                            if (typeof contentRef === "function") {
                                contentRef(r);
                            } else if (contentRef) {
                                (contentRef as React.MutableRefObject<HTMLDivElement | null>).current = r;
                            }
                        }}
                        style={isTransparent ? { padding: 0, margin: 0 } : {}}
                    >
                        {children}
                    </div>
                    {closeButton ? (
                        <IconButton 
                            className={styleClasses["lc-icon-button"]} 
                            style={{ top: 10, right: 10 }} 
                            onPress={() => {
                                setTimeout(() => {
                                    state.close();
                                }, 16);
                            }}
                        >
                            <Cross2Icon />
                        </IconButton>
                    ) : null}
                </div>
            </div>
        </Overlay>
    );
}


export default function Modal({ children, ...props }: ModalProps) {
    const state = useOverlayTriggerState(props);

    return state.isOpen ? (
        <ModalStateless {...props} state={state}>
            {typeof children === "function" ? (children(state.close) as ReactNode) : children}
        </ModalStateless>
    ) : null;
}


export const ModalTrigger = React.forwardRef<HTMLElement, ModalTriggerProps>(
    ({ modalClassName, className, content, children, onBeforePress = () => {}, ...props }, ref) => {
        const state = useOverlayTriggerState(props);

        return (
            <>
                <Pressable 
                    onPress={() => {
                        onBeforePress();
                        state.open();
                    }} 
                    ref={ref as any}
                >
                    {children}
                </Pressable>
                {state.isOpen && (
                    <ModalStateless className={modalClassName} {...props} state={state}>
                        {(typeof content === "function" ? content(state.close) : content) as ReactNode}
                    </ModalStateless>
                )}
            </>
        );
    }
);


// ==================== Modal Confirm ====================

function ModalConfirm({
    content,
    error,
    isProgressing,
    isReady = true,
    cancelText = DEFAULT_CANCEL_TEXT,
    okText = DEFAULT_OK_TEXT,
    okColor = "grass",
    onCancel,
    onOk,
    okAppearance,
    noCancel = false,
    style,
    open,
    onOpenChange,
}: ModalConfirmProps) {
    function renderContent() {
        return (
            <div className={"py-6 px-5 flex flex-col gap-4"}>
                {content}
                {error ? (
                    <div className={clsx("text-[--red-11] text-[small] px-4")}>{error}</div>
                ) : null}
                <div className={"flex justify-end gap-3"}>
                    {noCancel ? null : (
                        <Button onPress={() => onCancel?.()} isDisabled={isProgressing}>
                            {cancelText}
                        </Button>
                    )}
                    {onOk ? (
                        <Button 
                            className="!px-6" 
                            color={okColor as any} 
                            appearance={okAppearance || "primary"} 
                            isLoading={isProgressing} 
                            autoFocus 
                            isDisabled={!isReady} 
                            onPress={() => onOk()}
                        >
                            {okText}
                        </Button>
                    ) : null}
                </div>
            </div>
        );
    }

    return (
        <Modal
            isOpen={open}
            onOpenChange={onOpenChange}
            style={style}
            className="max-w-md w-[90vw]"
        >
            {renderContent()}
        </Modal>
    );
}


function ModalConfirmForImperative({ 
    content, 
    cancelText, 
    okText, 
    okColor, 
    okAppearance, 
    style, 
    onConfirm, 
    noCancel 
}: Omit<CreateModalConfirmOptions, 'onConfirm'> & { 
    onConfirm: (result: boolean, dismissed: boolean) => void 
}) {
    const [open, setOpen] = useState(true);
    const [isClosing, setIsClosing] = useState(false);
    const isMounted = useRef<boolean>(true);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const handleClose = (result: boolean, dismissed = false) => {
        if (isClosing || !isMounted.current) return;

        setIsClosing(true);
        setOpen((prev) => {
            if (!prev) return prev;
            return false;
        });

        try {
            onConfirm(result, dismissed);
        } catch (e) {
            console.warn('Error in confirm callback:', e);
        }
    };

    return (
        <ModalConfirm
            open={open}
            content={content}
            cancelText={cancelText}
            okText={okText}
            style={style}
            okColor={okColor}
            okAppearance={okAppearance}
            noCancel={noCancel}
            onOk={() => handleClose(true)}
            onCancel={() => handleClose(false)}
            onOpenChange={(open) => {
                if (!open) {
                    handleClose(false, true);
                }
            }}
        />
    );
}


export function createModalConfirm({
    cancelText = DEFAULT_CANCEL_TEXT,
    noCancel = false,
    okText = DEFAULT_OK_TEXT,
    okColor,
    content,
    onConfirm,
    okAppearance,
    style,
}: CreateModalConfirmOptions) {
    const container = document.createElement('div');
    let isUnmounting = false;
    
    document.body.appendChild(container);

    const root = ReactDOM.createRoot(container);

    function clear() {
        if (isUnmounting) return;
        isUnmounting = true;

        // 先卸载 React 组件
        try {
            root.unmount();
        } catch (e) {
            console.warn('Error unmounting modal confirm:', e);
        }

        // 延迟移除 DOM，确保 React 完成清理
        const maxRetries = 3;
        let retries = 0;

        const attemptRemoval = () => {
            try {
                // 检查容器是否仍然是 document.body 的直接子节点
                if (container && container.parentNode === document.body) {
                    document.body.removeChild(container);
                }
            } catch (e) {
                console.warn('Error clearing modal confirm container:', e);
                // 如果失败，重试几次
                if (retries < maxRetries) {
                    retries++;
                    setTimeout(attemptRemoval, 50 * retries);
                }
            }
        };

        // 给 React 一些时间完成清理
        setTimeout(attemptRemoval, 100);
    }

    root.render(
        <ModalConfirmForImperative
            content={content}
            cancelText={cancelText}
            okText={okText}
            noCancel={noCancel}
            style={style}
            okColor={okColor}
            okAppearance={okAppearance}
            onConfirm={(yes, dismissed) => {
                try {
                    onConfirm?.(yes, dismissed);
                } finally {
                    clear();
                }
            }}
        />
    );
}


// ==================== Modal Form ====================

export function ModalForm({
    onOpenChange,
    onDataCollected,
    onCancel,
    color,
    isSubmittable,
    additionalContent,
    okText = DEFAULT_OK_TEXT,
    fields: givenFields = [],
    title,
    description,
    showLabel = true,
}: ModalFormProps) {
    const fields = givenFields.filter((x) => !!x);

    const [open, setOpen] = useState(true);
    const [isClosing, setIsClosing] = useState(false);
    const isMounted = useRef<boolean>(true);

    const [editing, setEditing] = useState<Record<string, any>>(
        fields.reduce((acc, f) => {
            const { name, initialValue } = f;
            if (initialValue !== undefined) {
                return { ...acc, [name]: initialValue };
            } else {
                return acc;
            }
        }, {} as Record<string, any>)
    );

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const canSubmit = isSubmittable
        ? isSubmittable(editing)
        : fields.every((f) => {
              const { name, initialValue } = f;
              return editing[name] !== undefined && editing[name] != initialValue;
          });

    const [loading, setLoading] = useState(false);

    const handleClose = (open: boolean) => {
        if (!open && !isClosing && isMounted.current) {
            setIsClosing(true);
            setOpen(false);
            try {
                onOpenChange(false);
            } catch (e) {
                console.warn('Error in onOpenChange callback:', e);
            }
        }
    };

    function renderInput({
        name,
        type,
        value,
        onChange,
        placeholder,
        index,
        minRows = false,
        ...inputOptions
    }: {
        name: string;
        type?: string;
        value: any;
        onChange: (value: any) => void;
        placeholder?: string;
        index: number;
        minRows?: number | false;
        [key: string]: any;
    }) {
        if (type === "Boolean") {
            return (
                <div className="flex items-center gap-3">
                    <Checkbox checked={value || false} onChange={onChange} isDisabled={false} className="" />
                    <div>{name}</div>
                </div>
            );
        }

        if (type === "Password") {
            return (
                <PasswordInput
                    styled={true}
                    value={value || ""}
                    onChange={onChange}
                    placeholder={placeholder}
                    autoFocus={index === 0}
                />
            );
        }

        if (type === "Select") {
            return (
                <SelectInput
                    items={inputOptions.options}
                    isMulti={inputOptions.isMulti}
                    selected={value}
                    onSelect={onChange}
                    placeholder={placeholder}
                    autoFocus={index === 0}
                />
            );
        }

        if (minRows) {
            return (
                <TextArea
                    minRows={minRows}
                    styled={true}
                    autoFocus={index === 0}
                    color={"main"}
                    value={value || ""}
                    onChange={onChange}
                    placeholder={placeholder}
                    {...inputOptions}
                />
            );
        }

        return (
            <TextInput
                type="text"
                styled={true}
                value={value || ""}
                onChange={onChange}
                placeholder={placeholder}
                autoFocus={index === 0}
            />
        );
    }

    const content = (
        <div className="flex flex-col gap-5 px-6 py-5" data-color={color || "gray"}>
            <div className={clsx("font-size-16 font-medium")}>{title}</div>
            {description ? (
                <div className={clsx("font-size-13")}>{description}</div>
            ) : null}
            <div className="flex flex-col gap-4">
                {fields.map((field, index) => {
                    const { name, label, placeholder, type, minRows, ...inputOptions } = field;

                    return (
                        <fieldset className={clsx("flex flex-col")} key={name}>
                            {showLabel ? (
                                <label className={styleClasses["Label"]} htmlFor={name}>
                                    {label || name}
                                </label>
                            ) : null}

                            {renderInput({
                                name,
                                type,
                                value: editing[name],
                                placeholder,
                                onChange: (value) => {
                                    setEditing((prev) => ({
                                        ...prev,
                                        [name]: value,
                                    }));
                                },
                                index,
                                minRows,
                                ...inputOptions,
                            })}
                        </fieldset>
                    );
                })}
            </div>
            {additionalContent ? (
                <div className="flex flex-col gap-2">{additionalContent}</div>
            ) : null}
            <div className="flex justify-end">
                <Button
                    color="grass"
                    appearance={"primary"}
                    isDisabled={!canSubmit}
                    isLoading={loading}
                    onPress={async () => {
                        if (isClosing || !isMounted.current) return;
                        setLoading(true);
                        try {
                            await onDataCollected(editing);
                            if (isMounted.current) {
                                setLoading(false);
                                handleClose(false);
                            }
                        } catch (e) {
                            console.error("Error collecting data:", e);
                            if (isMounted.current) {
                                setLoading(false);
                            }
                        }
                    }}
                >
                    {okText}
                </Button>
            </div>
        </div>
    );

    return (
        <Modal
            isOpen={open}
            onOpenChange={(open) => {
                if (!open) {
                    onCancel?.();
                }
                handleClose(open);
            }}
            isDismissable
            className="max-w-md w-[90vw]"
        >
            {content}
        </Modal>
    );
}


export function createModalForm({
    okText = DEFAULT_OK_TEXT,
    fields,
    title,
    description,
    showLabel = true,
    color = "violet",
    isSubmittable = null,
    onDataCollected,
    onCancel = () => {},
    additionalContent = null,
}: CreateModalFormOptions) {
    const container = document.createElement('div');
    let isUnmounting = false;
    
    document.body.appendChild(container);

    const root = ReactDOM.createRoot(container);

    function clear() {
        if (isUnmounting) return;
        isUnmounting = true;

        // 先卸载 React 组件
        try {
            root.unmount();
        } catch (e) {
            console.warn('Error unmounting modal form:', e);
        }

        // 延迟移除 DOM，确保 React 完成清理
        const maxRetries = 3;
        let retries = 0;

        const attemptRemoval = () => {
            try {
                // 检查容器是否仍然是 document.body 的直接子节点
                if (container && container.parentNode === document.body) {
                    document.body.removeChild(container);
                }
            } catch (e) {
                console.warn('Error clearing modal form container:', e);
                // 如果失败，重试几次
                if (retries < maxRetries) {
                    retries++;
                    setTimeout(attemptRemoval, 50 * retries);
                }
            }
        };

        // 给 React 一些时间完成清理
        setTimeout(attemptRemoval, 100);
    }

    let formClosed = false;

    root.render(
        <ModalForm
            title={title}
            description={description}
            showLabel={showLabel}
            okText={okText}
            fields={fields}
            onDataCollected={onDataCollected}
            isSubmittable={isSubmittable || undefined}
            color={color}
            onCancel={onCancel}
            additionalContent={additionalContent}
            onOpenChange={(open) => {
                if (!open && !formClosed) {
                    formClosed = true;
                    try {
                        clear();
                    } catch (e) {
                        console.warn('Error during form cleanup:', e);
                    }
                }
            }}
        />
    );
}

